import {
  FloatingFocusManager,
  FloatingOverlay,
  FloatingPortal,
  useClick,
  useDismiss,
  useFloating,
  useInteractions,
  useRole,
} from '@floating-ui/react'
import { cx } from 'lib/cx'
import NextImage from 'next/image'
import type { CSSProperties } from 'react'
import { useState } from 'react'

const Lightbox = ({
  url,
  nextImageProps,
  className,
  style,
  disabled,
  children,
}: {
  url: string
  className?: string
  style?: CSSProperties
  disabled?: boolean
  nextImageProps?: Omit<React.ComponentProps<typeof NextImage>, 'src'>
  children?: React.ReactNode
}) => {
  const [isOpen, setOpen] = useState(false)
  const { refs, context } = useFloating({
    open: isOpen,
    onOpenChange: setOpen,
  })

  const click = useClick(context)
  const role = useRole(context)
  const dismiss = useDismiss(context, { outsidePressEvent: 'mousedown' })

  const { getReferenceProps } = useInteractions([click, role, dismiss])

  return (
    <div
      className={cx('focus bg-gray-10 relative mt-4 w-full', className)}
      style={style}
      ref={refs.setReference}
      {...(disabled ? {} : getReferenceProps())}
    >
      {children}
      <NextImage
        src={url}
        alt="Bild zum Post"
        sizes="(max-width: 668px) 90vw, 610px"
        fill
        {...nextImageProps}
        className={cx(
          'object-contain',
          !disabled &&
            'hover:bg-gray-10 cursor-pointer transition-all duration-200 hover:opacity-80',
          nextImageProps?.className,
        )}
      />
      {!disabled && (
        <FloatingPortal>
          {isOpen && (
            <FloatingOverlay
              className="z-20 grid place-items-center bg-black/50 p-3 md:p-32"
              lockScroll
            >
              <FloatingFocusManager context={context}>
                <div className="relative mt-14 h-full max-h-[90vh] w-full">
                  <NextImage
                    src={url}
                    alt="maximized post Image"
                    fill
                    {...nextImageProps}
                    className={cx(
                      'bg-white object-contain',
                      nextImageProps?.className,
                    )}
                  />
                </div>
              </FloatingFocusManager>
            </FloatingOverlay>
          )}
        </FloatingPortal>
      )}
    </div>
  )
}

export default Lightbox
